import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";

type User = {
  id: string;
  email: string;
  name: string;
  credits: number;
  created_at: string;
  daily_website_count: number;
  last_website_count_reset: string | null;
};

type AuthContextType = {
  token: string | null;
  user: User | null;
  setToken: (token: string | null) => void;
  setUser: (user: User | null) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  token: null,
  user: null,
  setToken: () => {},
  setUser: () => {},
  logout: () => {},
  refreshUser: async () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [token, setTokenState] = useState<string | null>(() =>
    localStorage.getItem("auth_token")
  );
  const [user, setUser] = useState<User | null>(null);

  // Keep localStorage in sync
  const setToken = (newToken: string | null) => {
    setTokenState(newToken);
    if (newToken) localStorage.setItem("auth_token", newToken);
    else localStorage.removeItem("auth_token");
  };

  const logout = () => {
    setToken(null);
    setUser(null);
  };

  // Fetch or re-fetch the current user
  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/user/me`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch user");
      const data: User = await res.json();
      setUser(data);
    } catch (err) {
      console.error("Failed to refresh user:", err);
      logout();
    }
  }, [token]);

  // Whenever the token changes, re-fetch the user
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  return (
    <AuthContext.Provider
      value={{ token, user, setToken, setUser, logout, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  return useContext(AuthContext);
}
